// src/components/elevenlabs/SFXMusicPage.tsx
// Sound effects + Music generation — both sync.

import { useState } from 'react';
import { Loader2, Music, Sparkles } from 'lucide-react';

import { generateSFX, generateMusic } from '@/lib/api_elevenlabs';
import { AudioPlayer } from '@/components/shared/AudioPlayer';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;

export function SFXMusicPage({ addNotification }: { addNotification?: Notifier }) {
  const [tab, setTab] = useState<'sfx' | 'music'>('sfx');

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Sub-tabs */}
      <div className="flex gap-1 p-1 bg-white/5 rounded-xl w-fit">
        {(['sfx', 'music'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-all ${
              tab === t ? 'bg-[var(--acc1)] text-white shadow' : 'text-white/50 hover:text-white/80'
            }`}
          >
            {t === 'sfx' ? 'Efectos de sonido' : 'Música'}
          </button>
        ))}
      </div>

      {tab === 'sfx' ? (
        <SFXForm addNotification={addNotification} />
      ) : (
        <MusicForm addNotification={addNotification} />
      )}
    </div>
  );
}

function SFXForm({ addNotification }: { addNotification?: Notifier }) {
  const [text, setText] = useState('');
  const [duration, setDuration] = useState(5);
  const [generating, setGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!text.trim()) return;
    setGenerating(true);
    setAudioUrl(null);
    try {
      const res = await generateSFX({ text: text.trim(), duration_seconds: duration });
      setAudioUrl(res.audio_url);
      addNotification?.({ type: 'success', title: 'SFX generado', message: `${res.duration.toFixed(1)}s` });
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'Error generando SFX', message: e.message });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs text-white/50 mb-1 block">Descripción del sonido</label>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--acc1)]"
          placeholder="ej: campana de iglesia reverberando en una catedral"
        />
      </div>

      <div>
        <label className="text-xs text-white/50 flex justify-between mb-1">
          Duración
          <span className="text-white/70 font-mono">{duration}s</span>
        </label>
        <input
          type="range"
          min={0.5}
          max={30}
          step={0.5}
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
          className="w-full accent-[var(--acc1)]"
        />
      </div>

      <button
        onClick={handleGenerate}
        disabled={generating || !text.trim()}
        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--acc1)] text-white font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
      >
        {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
        {generating ? 'Generando…' : 'Generar efecto'}
      </button>

      {audioUrl && <AudioPlayer src={audioUrl} label="SFX" />}
    </div>
  );
}

function MusicForm({ addNotification }: { addNotification?: Notifier }) {
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(30);
  const [instrumental, setInstrumental] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setAudioUrl(null);
    try {
      const res = await generateMusic({ prompt: prompt.trim(), duration_seconds: duration, instrumental });
      setAudioUrl(res.audio_url);
      addNotification?.({ type: 'success', title: 'Música generada', message: `${res.duration.toFixed(1)}s` });
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'Error generando música', message: e.message });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs text-white/50 mb-1 block">Prompt de música</label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--acc1)] resize-y"
          placeholder="ej: lofi hip-hop ambient para estudiar, tempo lento, piano suave"
        />
      </div>

      <div>
        <label className="text-xs text-white/50 flex justify-between mb-1">
          Duración
          <span className="text-white/70 font-mono">{duration}s</span>
        </label>
        <input
          type="range"
          min={3}
          max={600}
          step={1}
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
          className="w-full accent-[var(--acc1)]"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
        <input
          type="checkbox"
          checked={instrumental}
          onChange={(e) => setInstrumental(e.target.checked)}
          className="accent-[var(--acc1)]"
        />
        Solo instrumental (sin voz)
      </label>

      <button
        onClick={handleGenerate}
        disabled={generating || !prompt.trim()}
        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--acc1)] text-white font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
      >
        {generating ? <Loader2 size={16} className="animate-spin" /> : <Music size={16} />}
        {generating ? 'Generando…' : 'Generar música'}
      </button>

      {audioUrl && <AudioPlayer src={audioUrl} label="Música" />}
    </div>
  );
}
